import type { MacroOption } from '@pushly/aqe/lib/components'
import { TriggerType } from '@pushly/models/lib/enums/journeys'
import type { ExternalEventType } from '@pushly/models/lib/structs/external-events/external-event-type'
import type { NotificationBuilderMacroCategoryMap } from '../../../components/notification-builder/interfaces/notification-builder-state.interface'
import { eventPropertyDisplayName, eventPropertyPath } from './event-property-fields'

/**
 * The macro group event properties are offered under.
 *
 * Their own group rather than a share of the custom one, which holds a domain's discovered profile
 * attributes. Those are stored on a subscriber and live until they change; an event property lives
 * for the length of one event and is then gone. Listing them together tells the author the two are
 * interchangeable in a message, and they are not — one still renders next month, the other renders
 * only for the event that triggered the send.
 */
export const EVENT_MACRO_GROUP = 'event' as const

/**
 * The macros a message may interpolate the triggering event's properties through.
 *
 * The path a macro carries is the same one a condition binds to, because the router resolves both
 * against the same event: `event.properties.gate` in a body is `event.properties.gate` in a filter.
 * That is a wire fact rather than a naming convention, so nothing here is free to spell it
 * differently.
 *
 * Built from the event type's registered property map rather than from a list kept here. A partner
 * adds a property without a release, and a macro typed from memory is one nothing on the send path
 * can resolve — which is a message that goes out with a blank where the reason for sending it
 * should be.
 */
export function buildEventMacroOptions(eventType: ExternalEventType | undefined): MacroOption[] {
    if (!eventType?.properties?.length) return []

    return eventType.properties.map((property) => ({
        label: eventPropertyDisplayName(property),
        value: eventPropertyPath(property.key),
    }))
}

/**
 * Which macro groups a composer offers, for a journey with the given trigger.
 *
 * Stated per trigger rather than globally, because a macro nothing can resolve is worse than a
 * macro nobody was offered: every other trigger routes without an event, so `event.properties.gate`
 * in one of their messages renders blank at send time with nothing anywhere saying why.
 *
 * `undefined` leaves the composer on its own defaults, which is what every trigger that is not an
 * external event should see — the same groups, in the same order, as before this existed.
 */
export function macroCategoriesForTrigger(
    triggerType: TriggerType | undefined,
): NotificationBuilderMacroCategoryMap | undefined {
    if (triggerType !== TriggerType.EXTERNAL_EVENT) return undefined

    return {
        urlFields: ['domain', 'device', 'location', 'notification', 'secrets', 'custom', EVENT_MACRO_GROUP],
        messageFields: ['location', 'secrets', 'custom', EVENT_MACRO_GROUP],
    }
}
